import { useAppSignUpPageResultsUpdateEffect } from "@faire/retailer-visitor-shared/components/AppSignUp/AppSignUpPageResultsProvider";
import { LoadingProductTileSkeleton } from "@faire/retailer-visitor-shared/components/LoadingProductTileSkeleton/LoadingProductTileSkeleton";
import { PAGE_TYPE } from "@faire/retailer-visitor-shared/consts/PAGE_TYPE";
import { getAltTag } from "@faire/retailer-visitor-shared/features/Marketplace/utils";
import {
  ILeanProductTile,
  ILeanProductTileWithBrand,
} from "@faire/retailer-visitor-shared/features/MarketplaceItems/MarketplaceProduct/LeanProtos/ILeanProductTile";
import { ProductTile } from "@faire/retailer-visitor-shared/features/MarketplaceItems/ProductTile/index";
import { ProductTileType } from "@faire/retailer-visitor-shared/features/MarketplaceItems/ProductTile/types";
import { SaveToBoardProvider } from "@faire/retailer-visitor-shared/features/RetailerBoards/Context";
import { generateProductLinkPropsForLoggedOutRetailer } from "@faire/retailer-visitor-shared/lib/generateProductLinkPropsForLoggedOutRetailer";
import { brandPage } from "@faire/retailer-visitor-shared/paths/index";
import { useCategoryStore } from "@faire/retailer-visitor-shared/stores/domain/useCategoryStore";
import { LoggedOutCategoryPageItemList } from "@faire/retailer-visitor-shared/views/category/__internal__/CategoryPageRoot/__internal__/LoggedOutCategoryPage/__internal__/components/LoggedOutCategoryPageFilterAndResults/__internal__/LoggedOutCategoryPageItemList";
import { useLoggedOutCategoryPageCurrentCategory } from "@faire/retailer-visitor-shared/views/category/__internal__/CategoryPageRoot/__internal__/LoggedOutCategoryPage/__internal__/Context/useLoggedOutCategoryPageCurrentCategory";
import { useLoggedOutCategoryPageCatNavResponse } from "@faire/retailer-visitor-shared/views/category/__internal__/CategoryPageRoot/__internal__/LoggedOutCategoryPage/__internal__/DataFetching/useLoggedOutCategoryPageCatNav/useLoggedOutCategoryPageCatNavResponse";
import { useLoggedOutCategoryPageMetaTags } from "@faire/retailer-visitor-shared/views/category/__internal__/CategoryPageRoot/__internal__/LoggedOutCategoryPage/__internal__/DataFetching/useLoggedOutCategoryPageMetaTags";
import { trackCategoriesLoggedOutProductBatchView } from "@faire/web-api--source/events/categories/view/loggedOutProductBatch";
import { trackSeoCategoryProductListProductLinkRefsClick } from "@faire/web-api--source/events/seo/click/categoryProductListProductLinkRefs";
import { trackSignupModalOpenCatnavProductTileClick } from "@faire/web-api--source/events/signup/click/modalOpenCatnavProductTile";
import { ProductTileContainer } from "@faire/web-api--source/indigofair/data/ProductTileContainer";
import { ProductTileScreen } from "@faire/web-api--source/indigofair/data/ProductTileScreen";
import { ProductTileScreenContainer } from "@faire/web-api--source/indigofair/data/ProductTileScreenContainer";
import { useStrictLocalization } from "@faire/web/common/localization";
import { isNotUndefined } from "@faire/web/common/typescriptUtils";
import React, { useCallback } from "react";
import { useLocation } from "react-router-dom";

export const LoggedOutCategoryPageProductList: React.FC = () => {
  const location = useLocation();
  const { strictLocalize } = useStrictLocalization();
  const { seoCategoryText } = useLoggedOutCategoryPageMetaTags();
  const title = seoCategoryText?.h1 ?? "";
  const containerName = "logged_out_category_product_grid";
  const { shouldShowVerticalTile } = useCategoryStore();
  const categoryModel = useLoggedOutCategoryPageCurrentCategory();
  const {
    data: { requestId, brandPageQueryParams, productFirstSearchResults },
  } = useLoggedOutCategoryPageCatNavResponse();

  useAppSignUpPageResultsUpdateEffect({
    productTiles: productFirstSearchResults,
  });

  const onProductBatchVisible = useCallback(
    (items: Array<ILeanProductTile | undefined>) => {
      if (requestId) {
        trackCategoriesLoggedOutProductBatchView(
          requestId,
          items
            .map((i) => i?.product?.token)
            .filter(isNotUndefined)
            .join(" ")
        );
      }
    },
    [requestId]
  );

  const onClickProduct = useCallback(
    (productToken: string) => {
      trackSignupModalOpenCatnavProductTileClick();
      if (productToken) {
        const [c1UrlId = "", ...subcategoryUrlIds] =
          categoryModel.urlIdStrings ?? [];
        trackSeoCategoryProductListProductLinkRefsClick(
          productToken,
          // c1 UrlId
          c1UrlId,
          // remaining url IDs
          subcategoryUrlIds.join("|")
        );
      }
    },
    [categoryModel]
  );

  const renderItem = (tileData: ILeanProductTileWithBrand | undefined) => {
    if (!tileData) {
      return <LoadingProductTileSkeleton />;
    }

    return (
      <ProductTile
        product={tileData}
        type={ProductTileType.TILE_WITH_BRAND}
        brand={tileData.brand}
        imageConfigProps={{
          showVerticalImagery: shouldShowVerticalTile(
            categoryModel?.mostSpecificPathIdentifier
          ),
          altTag: getAltTag(
            {
              pageType: PAGE_TYPE.CATEGORY,
              productName: tileData?.product?.name,
              title,
            },
            strictLocalize
          ),
        }}
        isLoggedIn={false}
        tileInteractions={{
          tileActions: {
            onProductClick: onClickProduct,
          },
          props: {
            productLinkProps: generateProductLinkPropsForLoggedOutRetailer({
              productToken: tileData?.product?.token,
              brandToken: tileData?.brand?.token,
              location,
            }),
            brandLinkProps: {
              brandUrl: brandPage(
                tileData?.brand?.token ?? "",
                brandPageQueryParams
              ),
              brandToken: tileData?.brand?.token ?? "",
            },
          },
        }}
        loggingContext={{
          impressionProviderProps: {
            context: `${ProductTileContainer.CATEGORY_PRODUCT_RESULTS_GRID}|${categoryModel?.mostSpecificPathIdentifier}`,
            productTileContainer:
              ProductTileContainer.CATEGORY_PRODUCT_RESULTS_GRID,
            containerName,
            requestId,
            isNewProductsCategory: categoryModel?.isNewProducts,
            screenContext: ProductTileScreen.CATEGORY_PRODUCT_RESULTS,
            containerContext:
              ProductTileScreenContainer.CATEGORY_PRODUCT_RESULTS_PRODUCTS,
            contextToken: categoryModel?.mostSpecificPathIdentifier ?? "",
            trackedLinkContainerName: containerName,
          },
        }}
      />
    );
  };

  return (
    <SaveToBoardProvider value={{ trackOrigin: "logged-out-category-page" }}>
      <LoggedOutCategoryPageItemList
        items={productFirstSearchResults ?? []}
        renderItem={renderItem}
        onBatchVisible={onProductBatchVisible}
        hashKey={requestId}
      />
    </SaveToBoardProvider>
  );
};
